/** Debug overlay rendering enemy vision cones and flanking sound cones with wall-clipped rays. */
import Phaser from 'phaser';
import type { Position } from '../../utils/Types';
import { Enemy } from '../../entities/Enemy';
import { TILE_SIZE } from '../../utils/Constants';
import { worldToTile, directionToAngle, angleToDirection } from '../../utils/Math';
import { tileBlocksLOS } from '../../ai/Detection';

const CONE_ALPHA = 0.12;
const CONE_BORDER_ALPHA = 0.35;
const CONE_RAY_COUNT = 40;
const SOUND_CONE_COLOR = 0x2288cc;

const CONE_COLOR_IDLE = 0x00cc00;
const CONE_COLOR_ALERT = 0xcccc00;
const CONE_COLOR_CHASE = 0xcc2222;

/** Renders wall-clipped vision and sound cones with throttled raycasts (recompute every 3 frames, draw from cache). */
export class DetectionConeOverlay {
  private groundLayer: Phaser.Tilemaps.TilemapLayer;
  private frameCount = 0;
  private coneCache = new Map<Enemy, { vision: Position[]; sounds: Position[][]; visionColor: number }>();

  constructor(groundLayer: Phaser.Tilemaps.TilemapLayer) {
    this.groundLayer = groundLayer;
  }

  /** Draw vision and sound cones for every enemy, recomputing raycasts every 3 frames. */
  draw(graphics: Phaser.GameObjects.Graphics, enemies: readonly Enemy[]): void {
    this.frameCount++;
    const recompute = this.frameCount % 3 === 0;

    for (const enemy of enemies) {
      if (recompute || !this.coneCache.has(enemy)) {
        this.computeCones(enemy);
      }

      const cached = this.coneCache.get(enemy);
      if (!cached) continue;

      this.fillAndStrokeCone(graphics, cached.vision, cached.visionColor);
      for (const soundCone of cached.sounds) {
        this.fillAndStrokeCone(graphics, soundCone, SOUND_CONE_COLOR);
      }
    }

    // Prune cache for enemies no longer in the list
    if (recompute) {
      const enemySet = new Set(enemies);
      for (const key of this.coneCache.keys()) {
        if (!enemySet.has(key)) this.coneCache.delete(key);
      }
    }
  }

  private computeCones(enemy: Enemy): void {
    const ai = enemy.ai;
    const ex = enemy.sprite.x;
    const ey = enemy.sprite.y;
    const facingAngle = directionToAngle(ai.facingX, ai.facingY);

    // Vision cone
    const visionHalf = ai.visionHalfAngleRad;
    const visionStart = facingAngle - visionHalf;
    const visionStep = (visionHalf * 2) / CONE_RAY_COUNT;
    const vision = this.buildConePoints(ex, ey, visionStart, visionStep, CONE_RAY_COUNT, ai.detectionRadius);

    let visionColor: number;
    if (ai.state === 'chase' || ai.state === 'using_skill') {
      visionColor = CONE_COLOR_CHASE;
    } else if (ai.state === 'alert' || ai.aggro > 0) {
      visionColor = CONE_COLOR_ALERT;
    } else {
      visionColor = CONE_COLOR_IDLE;
    }

    // Sound cones
    const soundHalf = ai.soundConeHalfAngleRad;
    const soundOffset = ai.soundConeCenterRad;
    const soundRadius = ai.soundConeRangeVal;
    const sounds: Position[][] = [];
    for (const centerAngle of [facingAngle - soundOffset, facingAngle + soundOffset]) {
      const startAngle = centerAngle - soundHalf;
      const angleStep = (soundHalf * 2) / CONE_RAY_COUNT;
      sounds.push(this.buildConePoints(ex, ey, startAngle, angleStep, CONE_RAY_COUNT, soundRadius));
    }

    this.coneCache.set(enemy, { vision, sounds, visionColor });
  }

  private raycastToWall(ox: number, oy: number, angle: number, maxDist: number): number {
    const dir = angleToDirection(angle);
    const dx = dir.x;
    const dy = dir.y;
    const step = TILE_SIZE * 0.5;
    let dist = step;

    while (dist < maxDist) {
      const wx = ox + dx * dist;
      const wy = oy + dy * dist;
      const tx = worldToTile(wx);
      const ty = worldToTile(wy);
      const tile = this.groundLayer.getTileAt(tx, ty);
      if (tileBlocksLOS(tile)) return dist;
      dist += step;
    }

    return maxDist;
  }

  private buildConePoints(
    ex: number, ey: number, startAngle: number, angleStep: number,
    rayCount: number, maxRadius: number
  ): Position[] {
    const points: Position[] = [{ x: ex, y: ey }];
    for (let i = 0; i <= rayCount; i++) {
      const angle = startAngle + angleStep * i;
      const dist = this.raycastToWall(ex, ey, angle, maxRadius);
      const dir = angleToDirection(angle);
      points.push({
        x: ex + dir.x * dist,
        y: ey + dir.y * dist,
      });
    }
    return points;
  }

  private fillAndStrokeCone(
    graphics: Phaser.GameObjects.Graphics,
    points: Position[],
    color: number
  ): void {
    graphics.fillStyle(color, CONE_ALPHA);
    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.closePath();
    graphics.fillPath();

    graphics.lineStyle(1, color, CONE_BORDER_ALPHA);
    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.closePath();
    graphics.strokePath();
  }

  clear(): void {
    this.coneCache.clear();
  }

  destroy(): void {
    this.coneCache.clear();
  }
}
